/**
 * `/oms/suppression` — global cross-customer suppression view +
 * operator-only override (false-positive removal). OCP-136.
 *
 * Bulk-remove: select N rows, "Remove selected" → fans out
 * DELETE per selected id (no bulk endpoint on OMS side, so it's a
 * client-side fanout with progress feedback).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api, ApiError } from '../../lib/api';
import {
  StatusPill,
  UnconfiguredEmptyState,
  formatDate,
} from './CustomerDetailPage';

interface Suppression {
  id: string;
  email?: string | null;
  reason?: string | null;
  customer_id?: string | null;
  customer_email?: string | null;
  added_at?: string | null;
  added_by?: string | null;
}

interface SuppressionResponse {
  suppression: Suppression[];
  cursor?: string | null;
}

const REASON_OPTIONS = ['', 'bounce', 'complaint', 'manual'] as const;

export function OmsSuppressionPage() {
  const [params, setParams] = useSearchParams();
  const [customer, setCustomer] = useState(params.get('customer') ?? '');
  const [reason, setReason] = useState(params.get('reason') ?? '');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [data, setData] = useState<SuppressionResponse | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error' | 'unconfigured'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(
    null,
  );

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (customer) p.set('customer', customer);
    if (reason) p.set('reason', reason);
    if (email) p.set('email', email);
    if (cursor) p.set('cursor', cursor);
    p.set('limit', '100');
    return p.toString();
  }, [customer, reason, email, cursor]);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await api<SuppressionResponse>(`/v1/admin/oms/suppression?${qs}`);
      setData(r);
      setSelected(new Set());
      setState('idle');
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setState('unconfigured');
        return;
      }
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setErrorDetail(body?.detail ?? body?.error ?? `HTTP ${err.status}`);
      } else {
        setErrorDetail(err instanceof Error ? err.message : 'Unknown');
      }
      setState('error');
    }
  }, [qs]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (customer) p.set('customer', customer);
    if (reason) p.set('reason', reason);
    if (email) p.set('email', email);
    setParams(p, { replace: true });
  }, [customer, reason, email, setParams]);

  async function removeOne(id: string): Promise<boolean> {
    setActionError(null);
    try {
      await api(`/v1/admin/oms/suppression/${id}`, { method: 'DELETE' });
      return true;
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; detail?: string } | null;
        setActionError(body?.detail ?? body?.error ?? `HTTP ${err.status}`);
      } else {
        setActionError(err instanceof Error ? err.message : 'Remove failed');
      }
      return false;
    }
  }

  async function removeBulk() {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    setBulkProgress({ done: 0, total: ids.length });
    for (let i = 0; i < ids.length; i++) {
      await removeOne(ids[i]!);
      setBulkProgress({ done: i + 1, total: ids.length });
    }
    setBulkProgress(null);
    await load();
  }

  if (state === 'unconfigured') return <UnconfiguredEmptyState />;

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight">orboto Mail · Suppression</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Cross-customer suppression list. Remove a row to allow sending again —
          we will re-suppress bouncing addresses on the next attempt, so use only
          for confirmed false positives.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Customer (email/id)</span>
          <input
            type="search"
            value={customer}
            onChange={(e) => setCustomer(e.target.value)}
            className="w-56 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Email</span>
          <input
            type="search"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-56 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">Reason</span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          >
            {REASON_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o || 'All'}
              </option>
            ))}
          </select>
        </label>
        {selected.size > 0 && (
          <button
            type="button"
            onClick={() => void removeBulk()}
            disabled={bulkProgress !== null}
            className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:bg-gray-400"
          >
            {bulkProgress
              ? `Removing ${bulkProgress.done}/${bulkProgress.total}…`
              : `Remove ${selected.size} selected`}
          </button>
        )}
      </div>

      {actionError && (
        <div className="mb-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {actionError}
        </div>
      )}
      {state === 'error' && (
        <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 p-4">
          <h2 className="text-sm font-semibold text-red-900 dark:text-red-200">
            Could not load suppression
          </h2>
          {errorDetail && (
            <p className="mt-1 break-all text-xs text-red-800 dark:text-red-300">{errorDetail}</p>
          )}
        </div>
      )}

      {data && data.suppression.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="w-10 px-3 py-2">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={
                      data.suppression.length > 0 &&
                      selected.size === data.suppression.length
                    }
                    onChange={() =>
                      setSelected((prev) =>
                        prev.size === data.suppression.length
                          ? new Set()
                          : new Set(data.suppression.map((s) => s.id)),
                      )
                    }
                    className="h-4 w-4 accent-brand-500"
                  />
                </th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Customer</th>
                <th className="px-3 py-2">Added</th>
                <th className="px-3 py-2">By</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {data.suppression.map((s) => (
                <tr
                  key={s.id}
                  className={selected.has(s.id) ? 'bg-brand-50/50' : ''}
                >
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Select ${s.email ?? s.id}`}
                      checked={selected.has(s.id)}
                      onChange={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (next.has(s.id)) next.delete(s.id);
                          else next.add(s.id);
                          return next;
                        })
                      }
                      className="h-4 w-4 accent-brand-500"
                    />
                  </td>
                  <td className="px-3 py-2 font-medium">{s.email ?? '—'}</td>
                  <td className="px-3 py-2">
                    <StatusPill status={s.reason ?? null} />
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                    {s.customer_email ?? s.customer_id?.slice(0, 8) ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                    {s.added_at ? formatDate(s.added_at) : '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{s.added_by ?? '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={async () => {
                        const ok = await removeOne(s.id);
                        if (ok) await load();
                      }}
                      className="text-xs text-red-700 dark:text-red-300 hover:underline"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        state === 'idle' && (
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
            No suppression rows match the current filters.
          </div>
        )
      )}

      {data?.cursor && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => setCursor(data.cursor ?? undefined)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            Load more →
          </button>
        </div>
      )}
    </div>
  );
}
